/**
 * As contas de cor do mockup: as mesmas que o app faz para decidir o que é
 * legível sobre a cor da rede.
 *
 * Vivem aqui, e não dentro do `AppMockup`, porque as ilustrações das seções
 * (`components/homeSections`) precisam delas — e importar de volta do mockup
 * fecharia um ciclo entre os dois arquivos.
 *
 * CUIDADO: `resources/roletas/wheelGeometry.ts` exporta `luminance` com OUTRA
 * fórmula (brilho percebido 0.299/0.587/0.114, limiar 0.62) porque espelha o
 * `RoletaWheel/geometry.ts` do app. Os limiares daqui (sRGB linearizada) não
 * valem lá, nem o contrário.
 */

/** `#abc`, `#aabbcc` ou lixo → os três canais em 0–255. */
const channels = (hex: string) => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean.padEnd(6, '0');
  return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16) || 0);
};

export const luminance = (hex: string) => {
  const [r, g, b] = channels(hex).map(c => c / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/* Regra do WelcomeButton do app: texto branco, exceto quando o botão é branco —
   aí vira cinza. Fora desses dois, decide pelo contraste, senão botão amarelo
   sai com texto branco ilegível. */
export const buttonInk = (background: string) => {
  if (background.toUpperCase() === '#FFFFFF' || background.toUpperCase() === '#FFF') { return '#61616A'; }
  return luminance(background) > 0.45 ? '#16161F' : '#FFFFFF';
};

export const rgba = (hex: string, alpha: number) => {
  const [r, g, b] = channels(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * `amount` de `to` dentro de `from` — 0 devolve `from`, 1 devolve `to`.
 * Devolve hex para poder entrar de volta em `luminance` e em outro `mix`.
 */
export const mix = (from: string, to: string, amount: number) => {
  const [a, b] = [channels(from), channels(to)];
  const channel = (index: number) => Math.round(a[index] + (b[index] - a[index]) * amount)
    .toString(16).padStart(2, '0');
  return `#${channel(0)}${channel(1)}${channel(2)}`;
};
